import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form"
import {
  IProductPrice,
  IProductPricePaymentInstallment,
} from "../../../../core/types/Product"
import { TSale } from "../../../../core/types/Sale"
import React, { useMemo } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { TCheckoutLayout } from "../../../../core/types/Checkout"
import PaymentsByProduct6 from "./Payment"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import { TProductVoucherValue } from "../../../../core/types/Voucher"
import NumberFunctions from "../../../../core/functions/NumberFunctions"
import { useCheckoutContext } from "../../../../core/hooks/useCheckoutContext"
import BillingCountrySelect from "../../../Checkout/BillingCountrySelect"
import { checkoutPayment6Style } from "./Payment/PaymentConsts"

export interface ICheckoutPayments6 {
  layout?: TCheckoutLayout
  productPriceId: string
  loading: boolean
  loadingVoucher: boolean
  data: IProductPrice | null
  control: Control<TSale, any>
  watch: UseFormWatch<TSale>
  setValue: UseFormSetValue<TSale>
  errors: FieldErrors<TSale>
  installments?: IProductPricePaymentInstallment[]
  clearErrors: UseFormClearErrors<TSale>
  onApplyVoucher: () => void
  onRemoveVoucher: () => void
}

/**
 * Componente responsável por gerenciar o bloco de pagamentos no checkout do layout 4.
 */
const CheckoutPayments6 = ({
  layout,
  productPriceId,
  loading,
  loadingVoucher,
  data,
  control,
  watch,
  setValue,
  errors,
  installments,
  clearErrors,
  onApplyVoucher,
  onRemoveVoucher,
}: ICheckoutPayments6) => {
  const { productPrice, displayCurrency } = useCheckoutContext()

  const voucherIdentifier = watch("payment.voucherIdentifier")
  const voucherStatus = watch("payment.voucherStatus")
  const voucherType = watch("payment.voucherType")
  const voucherValue = watch("payment.voucherValue")

  const values = useMemo(
    () => ({
      paymentMethodsHtml: productPrice?.paymentMethods.map((pm, index) => {
        let concat = ""
        if (index < productPrice?.paymentMethods.length - 2) concat = ", "
        else if (index === productPrice?.paymentMethods.length - 2)
          concat = " ou no "

        return (
          <>
            {checkoutPayment6Style[pm.methodType].label}
            {concat}
          </>
        )
      }),
    }),
    [productPrice?.paymentMethods]
  )

  return (
    <Box sx={{ pt: 1 }}>
      {data?.hasVoucher && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 0.5,
            border: "1px solid #DCDDDE",
            borderRadius: "10px",
          }}
        >
          <Accordion
            elevation={0}
            data-cy="panelVoucherContent"
            sx={{ borderRadius: "12px" }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-voucher-content"
              id="panel-voucher-header"
            >
              {["none", "invalid"].includes(voucherStatus) && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocalOfferIcon
                    sx={{
                      color: "white",
                      stroke: "#323232",
                      strokeWidth: 1.5,
                      fontSize: "1.3rem",
                    }}
                  />
                  <Typography fontSize="14px" lineHeight="18px">
                    Tem um cupom de desconto?
                  </Typography>
                </Stack>
              )}
              {["valid"].includes(voucherStatus) && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleOutlineIcon
                    color="primary"
                    sx={{ fontSize: "1.3rem" }}
                  />
                  <Typography fontSize="14px" lineHeight="18px">
                    Cupom <strong>{voucherIdentifier}</strong> aplicado!
                  </Typography>
                </Stack>
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" spacing={2} alignItems="start">
                <TextField
                  fullWidth
                  size="small"
                  disabled={loadingVoucher || voucherStatus === "valid"}
                  {...control.register("payment.voucherIdentifier")}
                  error={voucherStatus === "invalid"}
                  helperText={
                    voucherStatus === "valid"
                      ? `Você recebeu um cupom de ${
                          voucherType === TProductVoucherValue.value
                            ? NumberFunctions.formatMoneyCurrency(voucherValue, displayCurrency)
                            : NumberFunctions.toPercentage(voucherValue)
                        }`
                      : ""
                  }
                  id="voucherIdentifier"
                />
                {["none", "invalid"].includes(voucherStatus) && (
                  <Button
                    id="buttonVoucherApply"
                    variant="contained"
                    size="large"
                    sx={{ textTransform: "none" }}
                    onClick={onApplyVoucher}
                    disabled={
                      loadingVoucher || (voucherIdentifier ?? "") === ""
                    }
                  >
                    Aplicar
                  </Button>
                )}
                {["valid"].includes(voucherStatus) && (
                  <Button
                    id="buttonVoucherRemove"
                    variant="outlined"
                    color="error"
                    size="large"
                    sx={{ textTransform: "none" }}
                    onClick={onRemoveVoucher}
                    disabled={loadingVoucher}
                  >
                    Excluir
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1, sm: 2 },
          border: "1px solid #DCDDDE",
          borderRadius: "10px",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <img src="/assets/layouts/v6/icon-pay-method.svg" alt="pay-method" />
          <Typography
            fontSize="20px"
            fontWeight={500}
            lineHeight="20px"
            sx={{ color: "#1F222C" }}
          >
            Formas de pagamento
          </Typography>
        </Stack>
        <Typography
          gutterBottom
          fontSize="14px"
          fontWeight={500}
          lineHeight="20px"
          sx={{ color: "#0F172A" }}
        >
          Pague no {values.paymentMethodsHtml}
        </Typography>
      <BillingCountrySelect />
        <PaymentsByProduct6
          data={data}
          loading={loading}
          control={control}
          setValue={setValue}
          watch={watch}
          errors={errors}
          installments={installments}
          clearErrors={clearErrors}
        />
      </Paper>
    </Box>
  )
}

export default CheckoutPayments6
